import arrayUnique from 'array-unique';

import type {
    DiffTreeDeletion,
    DiffTreeInsertion,
    DiffTreeModification,
} from '@gitbook/util-tree-merge';

import {
    type ComparePageOptions,
    comparePages,
    diffRevisionPageDocumentAttributes,
} from '../pages';
import {
    type ChangedRevisionReusableContentAttributes,
    type ChangedRevisionTagAttributes,
    type ChangedVariables,
    type ContentBranchHistoryItem,
    type Revision,
    type RevisionFileChangeAttributes,
    type RevisionPageDocumentChangeAttributes,
    type RevisionPageGroupChangeAttributes,
    type RevisionPageLinkChangeAttributes,
    RevisionPageType,
    type RevisionSemanticChange,
    RevisionSemanticChangeType,
} from '../types';
import { diffRevisionFiles, type RevisionTreeFile } from './diffRevisionFiles';
import { diffRevisionPages, type RevisionTreePage } from './diffRevisionPages';
import {
    getAbsolutePagePathInRevision,
    getPageParentInRevision,
    isRevisionPageParent,
} from './mutations';

/**
 * Return the semantic page/file changes between a revision and the previous revision.
 */
export function getRevisionSemanticChanges(
    revision: Revision,
    previousRevision: Revision,
    options: {
        /**
         * Limit the number of changes returned.
         */
        limit?: number;
    } & ComparePageOptions = {}
): Omit<ContentBranchHistoryItem, 'revision'> {
    const { limit = 10, ...compareOptions } = options;

    const semanticChanges: RevisionSemanticChange[] = [];
    const pagesDiff = Array.from(
        diffRevisionPages(revision, previousRevision, {
            includeDeleted: true,
            ...compareOptions,
        }).diffs.values()
    );
    const filesDiff = Array.from(
        diffRevisionFiles(revision, previousRevision, { includeDeleted: true }).diffs.values()
    );

    let more = 0;

    for (let index = 0; index < pagesDiff.length; index++) {
        const pageDiff = pagesDiff[index];
        switch (pageDiff.type) {
            case 'deleted': {
                semanticChanges.push(
                    getSemanticChangeForPageDeletion(revision, previousRevision, pageDiff)
                );
                break;
            }
            case 'added': {
                semanticChanges.push(
                    getSemanticChangeForPageAddition(revision, previousRevision, pageDiff)
                );
                break;
            }
            case 'modified': {
                const changes = getSemanticChangeForPageModification(revision, pageDiff);
                if (changes) {
                    semanticChanges.push(changes);
                }
                break;
            }
        }

        if (semanticChanges.length >= limit) {
            more += pagesDiff.length - index - 1;
            break;
        }
    }

    if (semanticChanges.length < limit) {
        for (let index = 0; index < filesDiff.length; index++) {
            const fileDiff = filesDiff[index];
            switch (fileDiff.type) {
                case 'deleted': {
                    semanticChanges.push({
                        type: RevisionSemanticChangeType.FileDeleted,
                        file: {
                            id: fileDiff.item.file.id,
                            name: fileDiff.item.file.name,
                            contentType: fileDiff.item.file.contentType,
                            downloadURL: fileDiff.item.file.downloadURL,
                        },
                    });
                    break;
                }
                case 'added': {
                    semanticChanges.push({
                        type: RevisionSemanticChangeType.FileCreated,
                        file: {
                            id: fileDiff.item.file.id,
                            name: fileDiff.item.file.name,
                            contentType: fileDiff.item.file.contentType,
                            downloadURL: fileDiff.item.file.downloadURL,
                        },
                    });
                    break;
                }
                case 'modified': {
                    semanticChanges.push(getSemanticChangesForModifiedFile(fileDiff));
                    break;
                }
            }

            if (semanticChanges.length >= limit) {
                more += filesDiff.length - index - 1;
                break;
            }
        }
    } else {
        more += filesDiff.length;
    }

    const variablesChanges = getSemanticChangesForRevisionVariables(revision, previousRevision);
    if (semanticChanges.length < limit) {
        semanticChanges.push(...variablesChanges);
    } else {
        more += variablesChanges.length;
    }

    const reusableContentChanges = getSemanticChangesForRevisionReusableContents(
        revision,
        previousRevision
    );
    if (semanticChanges.length < limit) {
        semanticChanges.push(...reusableContentChanges);
    } else {
        more += reusableContentChanges.length;
    }

    const tagChanges = getSemanticChangesForRevisionTags(revision, previousRevision);
    const room = Math.max(limit - semanticChanges.length, 0);
    semanticChanges.push(...tagChanges.slice(0, room));
    more += Math.max(tagChanges.length - room, 0);

    return {
        changes: semanticChanges,
        more,
    };
}

/** Counts of added, modified, and deleted entities in one revision comparison. */
export interface RevisionSemanticChangeCountsByType {
    added: number;
    modified: number;
    deleted: number;
}

/** Exact page and library entity counts for a revision comparison. */
export interface RevisionSemanticChangeCounts {
    pages: RevisionSemanticChangeCountsByType;
    library: RevisionSemanticChangeCountsByType;
}

/**
 * Count every changed page and library entity between a revision and the previous revision.
 * Unlike `getRevisionSemanticChanges`, this enumerates all changes, so reserve it for callers
 * that need exact counts.
 */
export function getRevisionSemanticChangeCounts(
    revision: Revision,
    previousRevision: Revision,
    options: ComparePageOptions = {}
): RevisionSemanticChangeCounts {
    const pageEntities = new Map<string, keyof RevisionSemanticChangeCountsByType>();
    const beforePositions = indexPagePositions(previousRevision);
    const afterPositions = indexPagePositions(revision);
    const { diffs } = diffRevisionPages(revision, previousRevision, options);
    for (const diff of diffs.values()) {
        const id = diff.item.page.id;
        const before = previousRevision.pages[id];
        const after = revision.pages[id];
        if (!before) {
            pageEntities.set(id, 'added');
        } else if (!after) {
            pageEntities.set(id, 'deleted');
        } else {
            const beforePosition = beforePositions.get(id);
            const afterPosition = afterPositions.get(id);
            if (
                comparePages(before, after, options) ||
                beforePosition?.parent !== afterPosition?.parent ||
                beforePosition?.index !== afterPosition?.index
            ) {
                pageEntities.set(id, 'modified');
            }
        }
    }

    const libraryEntities = new Map<string, keyof RevisionSemanticChangeCountsByType>();

    for (const diff of diffRevisionFiles(revision, previousRevision).diffs.values()) {
        libraryEntities.set(`file:${diff.item.file.id}`, diff.type);
    }
    const libraryChanges = [
        ...getSemanticChangesForRevisionVariables(revision, previousRevision),
        ...getSemanticChangesForRevisionReusableContents(revision, previousRevision),
        ...getSemanticChangesForRevisionTags(revision, previousRevision),
    ];
    for (const change of libraryChanges) {
        switch (change.type) {
            case RevisionSemanticChangeType.VariablesEdited:
                for (const [name, value] of Object.entries(change.variables)) {
                    libraryEntities.set(
                        `variable:${name}`,
                        value.before === undefined
                            ? 'added'
                            : value.after === undefined
                              ? 'deleted'
                              : 'modified'
                    );
                }
                break;
            case RevisionSemanticChangeType.ReusableContentCreated:
                libraryEntities.set(`reusable-content:${change.reusableContent.id}`, 'added');
                break;
            case RevisionSemanticChangeType.ReusableContentDeleted:
                libraryEntities.set(`reusable-content:${change.reusableContent.id}`, 'deleted');
                break;
            case RevisionSemanticChangeType.ReusableContentEdited:
                libraryEntities.set(`reusable-content:${change.reusableContent.id}`, 'modified');
                break;
            case RevisionSemanticChangeType.TagCreated:
                libraryEntities.set(`tag:${change.tag.slug}`, 'added');
                break;
            case RevisionSemanticChangeType.TagDeleted:
                libraryEntities.set(`tag:${change.tag.slug}`, 'deleted');
                break;
            case RevisionSemanticChangeType.TagEdited:
                libraryEntities.set(`tag:${change.tag.slug}`, 'modified');
                break;
        }
    }

    return {
        pages: countEntityStates(pageEntities.values()),
        library: countEntityStates(libraryEntities.values()),
    };
}

function indexPagePositions(
    revision: Revision
): Map<string, { parent: string | null; index: number }> {
    const positions = new Map<string, { parent: string | null; index: number }>();
    revision.rootPages.forEach((id, index) => positions.set(id, { parent: null, index }));
    for (const page of Object.values(revision.pages)) {
        if (isRevisionPageParent(page)) {
            page.pages.forEach((id, index) => positions.set(id, { parent: page.id, index }));
        }
    }
    return positions;
}

function countEntityStates(
    states: Iterable<keyof RevisionSemanticChangeCountsByType>
): RevisionSemanticChangeCountsByType {
    const counts = { added: 0, modified: 0, deleted: 0 };
    for (const state of states) {
        counts[state]++;
    }
    return counts;
}

/**
 * Return the semantic changes of an added page.
 *
 * A page tree insertion can also be a semantic page move (deletion + addition)
 */
function getSemanticChangeForPageAddition(
    revision: Revision,
    previousRevision: Revision,
    pageDiff: DiffTreeInsertion<RevisionTreePage>
): RevisionSemanticChange {
    const page = pageDiff.item.page;
    const pageInPreviousRevision =
        page.id in previousRevision.pages ? previousRevision.pages[page.id] : null;
    const partialPage = {
        id: page.id,
        type: page.type,
        title: page.title,
        path: isRevisionPageParent(page)
            ? getAbsolutePagePathInRevision(revision, page.id)
            : undefined,
    };

    if (!pageInPreviousRevision) {
        return {
            type: RevisionSemanticChangeType.PageCreated,
            page: partialPage,
        };
    }

    // Page was moved
    const parent = getPageParentInRevision(revision, page.id);
    const previousParent = getPageParentInRevision(previousRevision, page.id);

    return {
        type: RevisionSemanticChangeType.PageMoved,
        page: partialPage,
        parent: {
            before: previousParent
                ? {
                      id: previousParent.id,
                      type: previousParent.type,
                      title: previousParent.title,
                      path: isRevisionPageParent(previousParent)
                          ? getAbsolutePagePathInRevision(previousRevision, previousParent.id)
                          : undefined,
                  }
                : undefined,
            after: parent
                ? {
                      id: parent.id,
                      type: parent.type,
                      title: parent.title,
                      path: isRevisionPageParent(parent)
                          ? getAbsolutePagePathInRevision(revision, parent.id)
                          : undefined,
                  }
                : undefined,
        },
    };
}

/**
 * Return the semantic changes of a deleted page.
 *
 * A page tree deletion can also be a semantic page move (deletion + addition)
 */
function getSemanticChangeForPageDeletion(
    revision: Revision,
    previousRevision: Revision,
    pageDiff: DiffTreeDeletion<RevisionTreePage>
): RevisionSemanticChange {
    const page = pageDiff.item.page;
    const pageInRevision = page.id in revision.pages ? revision.pages[page.id] : null;

    // Page was deleted
    if (!pageInRevision) {
        return {
            type: RevisionSemanticChangeType.PageDeleted,
            page: {
                id: page.id,
                type: page.type,
                title: page.title,
                path: isRevisionPageParent(page)
                    ? getAbsolutePagePathInRevision(previousRevision, page.id)
                    : undefined,
            },
        };
    }

    // Page was moved
    const parent = getPageParentInRevision(revision, page.id);
    const previousParent = getPageParentInRevision(previousRevision, page.id);

    return {
        type: RevisionSemanticChangeType.PageMoved,
        page: {
            id: page.id,
            type: page.type,
            title: page.title,
            path: isRevisionPageParent(page)
                ? getAbsolutePagePathInRevision(revision, page.id)
                : undefined,
        },
        parent: {
            before: previousParent
                ? {
                      id: previousParent.id,
                      type: previousParent.type,
                      title: previousParent.title,
                      path: isRevisionPageParent(previousParent)
                          ? getAbsolutePagePathInRevision(previousRevision, previousParent.id)
                          : undefined,
                  }
                : undefined,
            after: parent
                ? {
                      id: parent.id,
                      type: parent.type,
                      title: parent.title,
                      path: isRevisionPageParent(parent)
                          ? getAbsolutePagePathInRevision(revision, parent.id)
                          : undefined,
                  }
                : undefined,
        },
    };
}

/**
 * Return the semantic changes of a modified file.
 */
function getSemanticChangesForModifiedFile(
    fileDiff: DiffTreeModification<RevisionTreeFile>
): RevisionSemanticChange {
    const { item, before } = fileDiff;
    const previousFile = before.file;
    const file = item.file;

    const changedAttributes: RevisionFileChangeAttributes = {};

    if (file.name !== previousFile.name) {
        changedAttributes.name = {
            before: previousFile.name,
            after: file.name,
        };
    }

    if (file.downloadURL !== previousFile.downloadURL) {
        changedAttributes.downloadURL = {
            before: previousFile.downloadURL,
            after: file.downloadURL,
        };
    }

    if (file.size !== previousFile.size) {
        changedAttributes.size = {
            before: previousFile.size,
            after: file.size,
        };
    }

    if (file.contentType !== previousFile.contentType) {
        changedAttributes.contentType = {
            before: previousFile.contentType,
            after: file.contentType,
        };
    }

    if (file.dimensions !== previousFile.dimensions) {
        changedAttributes.dimensions = {
            before: previousFile.dimensions,
            after: file.dimensions,
        };
    }

    return {
        type: RevisionSemanticChangeType.FileEdited,
        file: {
            id: file.id,
            name: file.name,
            contentType: file.contentType,
            downloadURL: fileDiff.item.file.downloadURL,
        },
        attributes: changedAttributes,
    };
}

/**
 * Return the semantic changes of a modified page.
 */
function getSemanticChangeForPageModification(
    revision: Revision,
    pageDiff: DiffTreeModification<RevisionTreePage>
): RevisionSemanticChange | undefined {
    const { item, before } = pageDiff;
    const previousPage = before.page;
    const page = item.page;

    if (
        page.type === RevisionPageType.Document &&
        previousPage.type === RevisionPageType.Document
    ) {
        const changedAttributes: RevisionPageDocumentChangeAttributes =
            diffRevisionPageDocumentAttributes(previousPage, page);

        if (page.document !== previousPage.document) {
            changedAttributes.document = {
                before: previousPage.document,
                after: page.document,
            };
        }

        return {
            type: RevisionSemanticChangeType.PageEdited,
            page: {
                id: page.id,
                type: page.type,
                title: page.title,
                path: getAbsolutePagePathInRevision(revision, page.id),
            },
            attributes: changedAttributes,
        };
    }
    if (page.type === RevisionPageType.Group && previousPage.type === RevisionPageType.Group) {
        const changedAttributes: RevisionPageGroupChangeAttributes = {};

        if (page.title !== previousPage.title) {
            changedAttributes.title = {
                before: previousPage.title,
                after: page.title,
            };
        }

        if (page.emoji !== previousPage.emoji) {
            changedAttributes.emoji = {
                before: previousPage.emoji,
                after: page.emoji,
            };
        }

        if (page.slug !== previousPage.slug) {
            changedAttributes.slug = {
                before: previousPage.slug,
                after: page.slug,
            };
        }

        if (Boolean(page.hidden) !== Boolean(previousPage.hidden)) {
            changedAttributes.hidden = {
                before: Boolean(previousPage.hidden),
                after: Boolean(page.hidden),
            };
        }

        if (Boolean(page.noIndex) !== Boolean(previousPage.noIndex)) {
            changedAttributes.noIndex = {
                before: Boolean(previousPage.noIndex),
                after: Boolean(page.noIndex),
            };
        }

        if (Boolean(page.noRobotsIndex) !== Boolean(previousPage.noRobotsIndex)) {
            changedAttributes.noRobotsIndex = {
                before: Boolean(previousPage.noRobotsIndex),
                after: Boolean(page.noRobotsIndex),
            };
        }

        if (page.condition !== previousPage.condition) {
            changedAttributes.condition = {
                before: previousPage.condition,
                after: page.condition,
            };
        }

        return {
            type: RevisionSemanticChangeType.PageEdited,
            page: {
                id: page.id,
                type: page.type,
                title: page.title,
                path: getAbsolutePagePathInRevision(revision, page.id),
            },
            attributes: changedAttributes,
        };
    }
    if (page.type === RevisionPageType.Link && previousPage.type === RevisionPageType.Link) {
        const changedAttributes: RevisionPageLinkChangeAttributes = {};

        if (page.title !== previousPage.title) {
            changedAttributes.title = {
                before: previousPage.title,
                after: page.title,
            };
        }

        if (page.emoji !== previousPage.emoji) {
            changedAttributes.emoji = {
                before: previousPage.emoji,
                after: page.emoji,
            };
        }

        if (page.target !== previousPage.target) {
            changedAttributes.target = {
                before: previousPage.target,
                after: page.target,
            };
        }

        if (page.condition !== previousPage.condition) {
            changedAttributes.condition = {
                before: previousPage.condition,
                after: page.condition,
            };
        }

        return {
            type: RevisionSemanticChangeType.PageEdited,
            page: {
                id: page.id,
                type: page.type,
                title: page.title,
                path: undefined,
            },
            attributes: changedAttributes,
        };
    }
}

/**
 * Return the semantic change for the variables of a revision.
 */
function getSemanticChangesForRevisionVariables(
    revision: Revision,
    previousRevision: Revision
): RevisionSemanticChange[] {
    const keys = arrayUnique([
        ...Object.keys(revision.variables ?? {}),
        ...Object.keys(previousRevision.variables ?? {}),
    ]);

    const changed: ChangedVariables = {};
    keys.forEach((key) => {
        const current = revision.variables?.[key];
        const previous = previousRevision.variables?.[key];

        if (current !== previous) {
            changed[key] = {
                before: previous,
                after: current,
            };
        }
    });

    if (Object.keys(changed).length === 0) {
        return [];
    }

    return [
        {
            type: RevisionSemanticChangeType.VariablesEdited,
            variables: changed,
        },
    ];
}

function getSemanticChangesForRevisionReusableContents(
    revision: Revision,
    previousRevision: Revision
): RevisionSemanticChange[] {
    const result: RevisionSemanticChange[] = [];

    Object.keys(revision.reusableContents ?? {}).forEach((key) => {
        const current = revision.reusableContents[key];
        const previous = previousRevision.reusableContents?.[key];

        if (!previous) {
            result.push({
                type: RevisionSemanticChangeType.ReusableContentCreated,
                reusableContent: current,
            });

            return;
        }

        const changedAttributes: ChangedRevisionReusableContentAttributes = {};

        if (previous.title !== current.title) {
            changedAttributes.title = {
                before: previous.title,
                after: current.title,
            };
        }

        if (previous.document !== current.document) {
            changedAttributes.document = {
                before: previous.document,
                after: current.document,
            };
        }

        if (Object.keys(changedAttributes).length > 0) {
            result.push({
                type: RevisionSemanticChangeType.ReusableContentEdited,
                reusableContent: current,
                attributes: changedAttributes,
            });
        }
    });

    Object.keys(previousRevision.reusableContents ?? {}).forEach((key) => {
        if (!revision.reusableContents?.[key]) {
            result.push({
                type: RevisionSemanticChangeType.ReusableContentDeleted,
                reusableContent: previousRevision.reusableContents[key],
            });
        }
    });

    return result;
}

function getSemanticChangesForRevisionTags(
    revision: Revision,
    previousRevision: Revision
): RevisionSemanticChange[] {
    const result: RevisionSemanticChange[] = [];
    const tags = revision.tags ?? {};
    const previousTags = previousRevision.tags ?? {};

    Object.keys(tags).forEach((slug) => {
        const current = tags[slug];
        const previous = Object.hasOwn(previousTags, slug) ? previousTags[slug] : undefined;

        if (!previous) {
            result.push({
                type: RevisionSemanticChangeType.TagCreated,
                tag: current,
            });

            return;
        }

        const changedAttributes: ChangedRevisionTagAttributes = {};

        if (previous.label !== current.label) {
            changedAttributes.label = {
                before: previous.label,
                after: current.label,
            };
        }

        if (Object.keys(changedAttributes).length > 0) {
            result.push({
                type: RevisionSemanticChangeType.TagEdited,
                tag: current,
                attributes: changedAttributes,
            });
        }
    });

    Object.keys(previousTags).forEach((slug) => {
        if (!Object.hasOwn(tags, slug)) {
            result.push({
                type: RevisionSemanticChangeType.TagDeleted,
                tag: previousTags[slug],
            });
        }
    });

    return result;
}
